import {
  type Interpreter,
  type ResolveTypegenMeta,
  type TypegenDisabled,
  type BaseActionObject,
  type ServiceMap,
} from 'xstate';
import { type CallParticipant } from '../../hooks/useCallParticipants';

export enum CallTransition {
  START = 'START',
  INIT_DONE = 'INIT_DONE',
  INIT_FAILED = 'INIT_FAILED',
  /**
   * When you lose internet connection
   */
  RECONNECTING = 'RECONNECTING',
  /**
   * When you reconnect
   */
  RECONNECTED = 'RECONNECTED',
  RESTART_INITIATED = 'RESTART_INITIATED',
  TRANSFER_TO_NEW_ROOM_INITIATED = 'TRANSFER_TO_NEW_ROOM_INITIATED',
  PARTICIPANT_JOINED = 'PARTICIPANT_JOINED',
  PARTICIPANT_USERNAME_TRANSFERRED = 'PARTICIPANT_USERNAME_TRANSFERRED',
  PARTICIPANTS_ALREADY_IN = 'PARTICIPANTS_ALREADY_IN',
  HANG_UP = 'HANG_UP',
  HOLD = 'HOLD',
  RESUME = 'RESUME',
  GROUP_CALL_PARTICIPANT_REMOVED = 'GROUP_CALL_PARTICIPANT_REMOVED',
  /**
   * When the last patient left (could be triggered only for provider).
   */
  LAST_PATIENT_LEFT = 'LAST_PATIENT_LEFT',
  /**
   * When a patient left during a group call.
   * Call still ongoing because there are others participants.
   */
  PATIENT_LEFT = 'PATIENT_LEFT',
  PROVIDER_LEFT = 'PROVIDER_LEFT',
  HANG_UP_ON_HOLD = 'HANG_UP_ON_HOLD',
  REDIRECTING_TO_DASHBOARD = 'REDIRECTING_TO_DASHBOARD',
  /**
   * Test call related transitions.
   */
  START_TEST_CALL = 'START_TEST_CALL',
}

export enum CallState {
  /**
   * The useCallState singleton hook is not yet mounted.
   * Do not send a transition yet: it would not be received.
   *
   * All other states (see below) indicate the useCallState singleton hook is mounted.
   */
  Unmounted = 'unmounted',

  /**
   * The state machine's initial state.
   * There is no call currently.
   * Ready to start a call.
   */
  Idle = 'idle',

  /**
   * First step in the process of starting a call.
   */
  Initializing = 'callInitializing',
  Restarting = 'callRestarting',
  Reconnecting = 'callReconnecting',
  WaitingForParticipants = 'callWaitingForParticipants',
  WaitingForParticipantsAfterRestart = 'callWaitingForParticipantsAfterRestart',
  InProgress = 'callInProgress',
  OnHold = 'callOnHold',
  Leaving = 'callLeaving',
  LeavingAfterError = 'callLeavingAfterError',
  LeavingAfterTransferToNewRoom = 'callLeavingAfterTransferToNewRoom',
  RedirectingToDashboard = 'redirectingToDashboard',
  /**
   * Test call related states.
   */
  TestCallInitializing = 'testCallInitializing',
  TestCallLeavingAfterError = 'testCallLeavingAfterError',
  TestCallLeaving = 'testCallLeaving',
  TestCallInProgress = 'testCallInProgress',
}

export type CallStateLeavingSubState = {
  [CallState.Leaving]:
    | 'groupCallParticipantRemoved'
    | 'providerLeft'
    | 'lastPatientLeft'
    | 'hangUp'
    | 'hangUpOnHold';
};

export type CallInProgressSubState = {
  [CallState.InProgress]: 'running' | 'reconnecting';
};

export type CallStateLeavingAfterErrorSubState = {
  [CallState.LeavingAfterError]: 'timeout' | 'error';
};

export type CallStates =
  | { value: CallState.Initializing; context: never }
  | { value: CallState.Restarting; context: never }
  | { value: CallState.Reconnecting; context: never }
  | { value: CallState.WaitingForParticipants; context: never }
  | { value: CallState.WaitingForParticipantsAfterRestart; context: never }
  | { value: CallState.InProgress; context: never }
  | {
      value: CallState.LeavingAfterError | CallStateLeavingAfterErrorSubState;
      context: never;
    }
  | {
      value: CallState.Leaving | CallStateLeavingSubState;
      context: never;
    }
  | {
      value: CallState.LeavingAfterTransferToNewRoom;
      context: never;
    }
  /**
   * Test call related transitions
   */
  | {
      value: CallState.TestCallInitializing;
      context: never;
    }
  | {
      value: CallState.TestCallInProgress;
      context: never;
    }
  | {
      value: CallState.TestCallLeaving;
      context: never;
    }
  | {
      value: CallState.TestCallLeavingAfterError;
      context: never;
    };

export type LastParticipantLeftEvent = {
  type: CallTransition.LAST_PATIENT_LEFT;
  participant: CallParticipant;
};

export type PersonLeftEvent = {
  type: CallTransition.PATIENT_LEFT;
  participant: CallParticipant;
};

export type ParticipantJoinedEvent = {
  type: CallTransition.PARTICIPANT_JOINED;
  participant: CallParticipant;
};

export type ParticipantUsernameTransferredEvent = {
  type: CallTransition.PARTICIPANT_USERNAME_TRANSFERRED;
  participant: CallParticipant;
};

export type CallEvents =
  | { type: CallTransition.START }
  | { type: CallTransition.INIT_DONE }
  | { type: CallTransition.INIT_FAILED }
  | ParticipantJoinedEvent
  | ParticipantUsernameTransferredEvent
  | { type: CallTransition.PARTICIPANTS_ALREADY_IN }
  | { type: CallTransition.HANG_UP }
  | { type: CallTransition.HOLD }
  | { type: CallTransition.RESUME }
  | { type: CallTransition.GROUP_CALL_PARTICIPANT_REMOVED }
  | { type: CallTransition.PROVIDER_LEFT }
  | { type: CallTransition.HANG_UP_ON_HOLD }
  | LastParticipantLeftEvent
  | PersonLeftEvent
  | { type: CallTransition.RESTART_INITIATED }
  | { type: CallTransition.RECONNECTING }
  | { type: CallTransition.RECONNECTED }
  | { type: CallTransition.REDIRECTING_TO_DASHBOARD }
  | { type: CallTransition.TRANSFER_TO_NEW_ROOM_INITIATED }
  /**
   * Test call related events.
   */
  | { type: CallTransition.START_TEST_CALL };

export type CallStateMachine = Interpreter<
  unknown,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  any,
  CallEvents,
  CallStates,
  ResolveTypegenMeta<TypegenDisabled, CallEvents, BaseActionObject, ServiceMap>
>;
